import { onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import api from '@/api/axiosBase';

// Lightweight function to inject personalized examples into static landing DOM (#landing-root)
// Conditions: only run on path === '/', userStore.idToken present, and only once per load.
let alreadyRan = false;

interface ProjectSummary {
  simpleID: string;
  siteUrl: string;
  createdAt: string;
  projectName?: string;
}

interface RawDesignVersion {
  subject?: string;
  versionID?: string;
  htmlContent?: string;
  htmlContentWithTestData?: string;
}

interface RawDesign {
  name?: string;
  projectName?: string;
  versions: RawDesignVersion[];
  activeVersionID?: string;
}

function pickDesign(designs: RawDesign[]) {
  const usable = designs.filter((d) => d.versions && d.versions.length);
  if (!usable.length) return null;
  const d = usable[Math.floor(Math.random() * usable.length)];
  let v = null;
  if (d.activeVersionID) {
    v =
      d.versions.find(
        (ver: RawDesignVersion) => ver.versionID === d.activeVersionID
      ) || null;
  }
  if (!v) v = d.versions[d.versions.length - 1] || {};
  return {
    subject: v.subject || d.name || 'Untitled Email',
    html: v.htmlContentWithTestData || v.htmlContent || '',
    projectName: d.projectName || ''
  };
}

export function useLandingPersonalization() {
  const route = useRoute();
  const userStore = useUserStore();

  async function run() {
    if (alreadyRan) return;
    if (route.path !== '/') return;
    if (!userStore.idToken) return;
    const grid = document.querySelector('#examples .grid, .examples .grid');
    if (!grid) return;
    try {
      alreadyRan = true;
      // 1. Fetch projects via /v2/agent/auth
      const authResp = await api.post('/v2/agent/auth', { Extras: ['P1'] });
      const projects: ProjectSummary[] = Array.isArray(authResp.data?.Projects)
        ? authResp.data.Projects
        : [];
      if (!projects.length) return;
      // newest first
      projects.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const chosen = projects.slice(0, 3);

      // 2. Fetch details for each chosen project (in parallel)
      type DetailResult = {
        base: ProjectSummary;
        data: {
          projectName?: string;
          siteUrl?: string;
          sendgridDesigns?: RawDesign[];
        };
      } | null;
      const detailResults: DetailResult[] = await Promise.all(
        chosen.map(async (p) => {
          try {
            const r = await api.get(`/v2/project/${p.simpleID}`);
            return { base: p, data: r.data };
          } catch {
            return null;
          }
        })
      );

      const withDesigns = detailResults.filter(
        (x) =>
          x &&
          Array.isArray(x.data?.sendgridDesigns) &&
          x.data.sendgridDesigns.some(
            (d: RawDesign) => Array.isArray(d.versions) && d.versions.length
          )
      );
      if (!withDesigns.length) return;

      const cards = (withDesigns as DetailResult[]).map((w) => {
        const data =
          w && w.data
            ? w.data
            : {
                projectName: undefined,
                siteUrl: undefined,
                sendgridDesigns: []
              };
        const t = pickDesign(data.sendgridDesigns || []) || {
          subject: 'Generated email',
          html: '',
          projectName: ''
        };
        return {
          subject: t.subject,
          html: t.html,
          projectName: data.projectName || data.siteUrl || 'Project'
        };
      });

      // Keep first existing default example if present; otherwise we create one placeholder.
      let children = Array.from(grid.children);
      if (!children.length) {
        const placeholder = document.createElement('article');
        placeholder.className = 'example-card';
        placeholder.innerHTML =
          '<div class="example-meta"><strong>Example</strong><span>Filename: example.html</span></div>' +
          '<div class="email-thumb"><div class="banner"></div><div class="body">' +
          '<div class="line wide"></div><div class="line mid"></div><div class="line short"></div>' +
          '</div></div>';
        grid.appendChild(placeholder);
        children = [placeholder];
      }
      // Remove all but the first
      children.slice(1).forEach((el) => el.remove());
      // Insert personalized cards (1..3 based on tenant count with templates)
      cards.slice(0, withDesigns.length).forEach((c) => {
        const art = document.createElement('article');
        art.className = 'example-card personalized-example';
        // Force card to allow full-bleed iframe usage
        art.style.display = 'flex';
        art.style.flexDirection = 'column';
        art.style.alignItems = 'stretch';
        art.style.justifyContent = 'flex-start';
        art.style.padding = '8px 8px 10px';
        art.style.boxSizing = 'border-box';
        art.style.height = '100%'; // in case parent grid uses equal row heights
        art.style.overflow = 'hidden';
        // Header meta
        const meta = document.createElement('div');
        meta.className = 'example-meta';
        meta.style.flexDirection = 'column';
        meta.style.alignItems = 'flex-start';
        meta.style.gap = '2px';
        meta.style.marginBottom = '4px';
        meta.style.flex = '0 0 auto';
        const subj = document.createElement('strong');
        subj.style.fontSize = '12px';
        subj.style.color = '#111';
        subj.style.whiteSpace = 'nowrap';
        subj.style.overflow = 'hidden';
        subj.style.textOverflow = 'ellipsis';
        subj.style.maxWidth = '100%';
        subj.textContent = c.subject;
        const from = document.createElement('span');
        from.style.fontSize = '10px';
        from.style.color = '#2563eb';
        from.style.fontWeight = '600';
        from.textContent = 'From: ' + c.projectName;
        meta.appendChild(subj);
        meta.appendChild(from);

        // Basic sanitization (remove scripts & inline event handlers)
        function sanitize(html: string) {
          let safe = html.replace(/<script[\s\S]*?<\/script>/gi, '');
          safe = safe.replace(/on\w+="[^"]*"/g, '');
          safe = safe.replace(/on\w+='[^']*'/g, '');
          return safe;
        }
        // Try to encourage full-width rendering by stripping common fixed width attributes
        function normalizeForFullWidth(html: string) {
          // Remove hard width attributes like width="600" or width="640" on tables
          let out = html.replace(/width="(600|640|580|650)"/gi, '');
          // Inline styles specifying fixed pixel width -> make them percentage
          out = out.replace(/width:\s?(600|640|580|650)px/gi, 'width:100%');
          // Center wrapper tables often have align=center; keep but allow expansion
          return out;
        }
        const emailHtml = normalizeForFullWidth(sanitize(c.html || ''));
        const MIN_IFRAME_HEIGHT = 160;
        const MAX_IFRAME_HEIGHT = 420; // target card height cap
        const iframe = document.createElement('iframe');
        iframe.setAttribute('title', 'Email preview');
        iframe.setAttribute('loading', 'lazy');
        // allow-same-origin so we can measure heights for dynamic resize (scripts still stripped)
        iframe.setAttribute('sandbox', 'allow-same-origin');
        iframe.style.width = '100%';
        iframe.style.display = 'block';
        iframe.style.flex = '1 1 auto';
        iframe.style.minHeight = MIN_IFRAME_HEIGHT + 'px';
        iframe.style.height = MAX_IFRAME_HEIGHT + 'px';
        iframe.style.border = '1px solid #e7edf6';
        iframe.style.borderRadius = '10px';
        iframe.style.background = '#fff';
        iframe.style.overflow = 'hidden';
        iframe.srcdoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><base target="_blank"/><style>
          html,body{margin:0;padding:0;width:100%!important;min-width:100%!important;}
          body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;line-height:1.4; padding:0 !important;}
          table{border-collapse:collapse;}
          table[width],table[style*="width"],table[style*="600px"],table[style*="640px"],table[style*="580px"],table[style*="650px"]{width:100%!important;max-width:100%!important;}
          img{max-width:100%!important;height:auto!important;display:block;}
          a{text-decoration:none;color:#2563eb;}
          /* Override common container max-width patterns */
          [style*="max-width:600px"],[style*="width:600px"],[style*="width:640px"],[style*="width:580px"],[style*="width:650px"], .container, .wrapper{max-width:100%!important;width:100%!important;}
          body > center, body > div[align="center"], body > table[align="center"]{margin:0!important;}
          /* Remove outer padding wrappers */
          [style*="padding:40px"],[style*="padding:30px"],[style*="padding-left:40px"],[style*="padding-right:40px"]{padding-left:20px!important;padding-right:20px!important;}
        </style></head><body>${emailHtml}</body></html>`;

        art.appendChild(meta);
        art.appendChild(iframe);
        // Auto-resize iframe height to content (capped)
        function resize(force = false) {
          try {
            const doc = iframe.contentDocument;
            if (!doc) return;
            const body = doc.body;
            const htmlEl = doc.documentElement;
            if (!body || !htmlEl) return;
            const candidate = Math.max(
              body.scrollHeight,
              body.offsetHeight,
              htmlEl.scrollHeight,
              htmlEl.offsetHeight,
              htmlEl.clientHeight
            );
            if (candidate) {
              const desired = Math.max(
                MIN_IFRAME_HEIGHT,
                Math.min(candidate + 4, MAX_IFRAME_HEIGHT)
              );
              if (force || Math.abs(desired - iframe.clientHeight) > 6) {
                iframe.style.height = desired + 'px';
              }
            }
          } catch {
            /* ignore */
          }
        }
        // Post-load full-bleed normalization
        function enforceFullBleed() {
          try {
            const doc = iframe.contentDocument;
            if (!doc) return;
            // Strip fixed widths on common block-level/table elements
            const selectors = ['table', 'td', 'tr', 'div', 'section', 'center'];
            selectors.forEach((sel) => {
              doc.querySelectorAll(sel).forEach((el) => {
                const e = el as HTMLElement;
                if (
                  e.getAttribute('width') &&
                  /^(600|640|650|580)$/i.test(e.getAttribute('width') || '')
                ) {
                  e.removeAttribute('width');
                }
                const style = e.getAttribute('style') || '';
                if (
                  /width\s*:\s*(600|640|650|580)px/i.test(style) ||
                  /max-width\s*:\s*(600|640|650|580)px/i.test(style)
                ) {
                  e.style.width = '100%';
                  e.style.maxWidth = '100%';
                }
              });
            });
            doc.body.style.margin = '0';
            doc.body.style.padding = '0';
          } catch {
            /* ignore */
          }
        }
        iframe.addEventListener('load', () => {
          enforceFullBleed();
          resize(true);
          // Observe image loads inside iframe for dynamic growth
          try {
            const doc = iframe.contentDocument;
            if (doc) {
              const imgs = Array.from(doc.images || []);
              imgs.forEach((img) => {
                if (!img.complete) {
                  img.addEventListener('load', () => {
                    enforceFullBleed();
                    resize(true);
                  });
                  img.addEventListener('error', () => resize(true));
                }
              });
            }
          } catch {
            /* ignore */
          }
          // extended retry window for slow assets + progressive normalization
          let attempts = 0;
          const id = setInterval(() => {
            attempts++;
            enforceFullBleed();
            resize();
            if (attempts > 8) clearInterval(id);
          }, 400);
        });
        grid.appendChild(art);
      });

      // Adjust nav login link (only once)
      document.querySelectorAll('#landing-root nav a').forEach((a) => {
        if (/login/i.test(a.textContent || '')) {
          a.textContent = 'Dashboard';
          a.setAttribute('href', '/projects');
        }
      });
    } catch (e) {
      // Fail silently to avoid disrupting landing
      alreadyRan = false; // allow retry on next navigation/mount
      console.warn('[useLandingPersonalization] failed', e);
    }
  }

  onMounted(run);

  // Retry when token or route changes, small debounce by using watch flush:post
  watch(
    () => [route.path, userStore.idToken],
    () => run(),
    { flush: 'post' }
  );
}
